/**
 * Workspace de Resolução — dashboard operacional (/problemas/:id)
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { RootCauseWizard } from '../components/problemas/RootCauseWizard';
import { ProblemValidationWizard } from '../components/problemas/ProblemValidationWizard';
import { ProblemTimeline } from '../components/problemas/ProblemTimeline';
import { EfficacyVerificationPanel } from '../components/problemas/EfficacyVerificationPanel';
import { canCloseIssue, efficacyAttempts, evaluateIssueClosure, EFFICACY_CONFIRMATION_CLOSES_ISSUE, readyForEfficacyCheck } from '../utils/efficacyCheck';
import { closureReasonPatch, normalizeRequiredReason } from '../utils/problemReason';
import { ProblemReasonModal } from '../components/problemas/ProblemReasonModal';
import { NewOpportunityModal } from '../components/oportunidades/NewOpportunityModal';
import { useIssueOpportunities } from '../hooks/useOpportunities';
import { openOpportunityCount, openOpportunityNotice, problemOpportunityDraft } from '../utils/problemOpportunity';
import type { Opportunity } from '../types/opportunity';
import { ProblemActionsTable } from '../components/problemas/ProblemActionsTable';
import { ProblemActionDrawer } from '../components/problemas/ProblemActionDrawer';
import { ProblemExecutionPanel } from '../components/problemas/ProblemExecutionPanel';
import { ProblemWorkspaceHeader } from '../components/problemas/ProblemWorkspaceHeader';
import { ProblemCaseWorkspace } from '../components/problemas/ProblemCaseWorkspace';
import { NewActionModal } from '../modules/actions/components/NewActionModal';
import { useAppToast } from '../components/AppToast';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useDataScope } from '../hooks/useDataScope';
import { useAuth } from '../context/AuthContext';
import { useOrgMembers } from '../hooks/useOrgMembers';
import { IssueService } from '../services/issueService';
import {
  createControlMeasure,
  createGembaNote,
  createLossMeasure,
  createSlice,
  createStratum,
  deleteGembaNote,
  deleteIssueMeasure,
  deleteSlice,
  deleteStratum,
  addMeasurePoint,
  createIssueMaspEvent,
  createStandardizationEvent,
  listIssueMaspEvents,
  listStandardizationEvents,
  loadIssueMaspBoard,
  readCauseCheck,
  saveCauseCheck,
} from '../services/issueMaspService';
import { addIssueEvidence, deleteIssueEvidence, listHypothesisEvidence, listIssueEvidence } from '../services/issueEvidenceService';
import type { MaspEditors } from '../components/problemas/ProblemStagePane';
import { ProjectService } from '../services/projectService';
import { enrichIssue, getIssueHistory } from '../services/problemDashboardData';
import { getIssueNextAction } from '../utils/issueNextAction';
import { resolveIssueAssigneeName } from '../utils/issueAssignee';
import { workspaceShowsResolutionCaveat, workspaceWorkFocus } from '../utils/problemWorkspaceView';
import { ISSUE_STATUS_LABELS } from '../types/issue';
import type { IssueStatus, RootCauseAnalysis } from '../types/issue';
import { ActionService, type EnterpriseAction } from '../services/actionService';
import { listActionCountermeasures } from '../services/actionCountermeasureService';

type EditView = null | 'causa' | 'acoes' | 'execucao' | 'validacao' | 'historico';

export const ProblemWorkspacePage: React.FC = () => {
  const { id: issueId } = useParams<{ id: string }>();
  const organizationId = useOrganizationId();
  const { user } = useAuth();
  const { roleType } = useDataScope();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();
  const { members: orgMembers } = useOrgMembers(organizationId);

  const [editView, setEditView] = useState<EditView>(null);
  const [rcaSaving, setRcaSaving] = useState(false);
  const [drawerActionId, setDrawerActionId] = useState<string | null>(null);
  const [showNewActionModal, setShowNewActionModal] = useState(false);
  const [editingActionId, setEditingActionId] = useState<string | null>(null);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [reasonIntent, setReasonIntent] = useState<null | 'save' | 'close' | 'reopen'>(null);

  const issueQuery = useQuery({
    queryKey: ['issue', organizationId, issueId],
    enabled: Boolean(organizationId && issueId),
    queryFn: () => IssueService.getById(organizationId!, issueId!),
  });

  const issueOpportunities = useIssueOpportunities(organizationId, issueId);

  const maspQuery = useQuery({
    queryKey: ['issue-masp-board', organizationId, issueId],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => loadIssueMaspBoard(organizationId!, issueId!),
  });

  const evidenceQuery = useQuery({
    queryKey: ['issue-evidence', organizationId, issueId, 'analysis'],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => listIssueEvidence(organizationId!, issueId!, 'analysis'),
  });

  const causeEvidenceQuery = useQuery({
    queryKey: ['issue-evidence', organizationId, issueId, 'root'],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => listHypothesisEvidence(organizationId!, issueId!),
  });

  const causeCheckQuery = useQuery({
    queryKey: ['issue-cause-check', organizationId, issueId],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => readCauseCheck(organizationId!, issueId!),
  });

  const countermeasuresQuery = useQuery({
    queryKey: ['issue-countermeasures', organizationId, issueId],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => listActionCountermeasures(organizationId!, issueId!),
  });

  const executionEventsQuery = useQuery({
    queryKey: ['issue-masp-events', organizationId, issueId],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => listIssueMaspEvents(organizationId!, issueId!),
  });

  const standardizationEventsQuery = useQuery({
    queryKey: ['issue-masp-events', organizationId, issueId, 'standardization'],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => listStandardizationEvents(organizationId!, issueId!),
  });

  const executionEvidenceQuery = useQuery({
    queryKey: ['issue-evidence', organizationId, issueId, 'exec'],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => listIssueEvidence(organizationId!, issueId!, 'exec'),
  });

  const validationEvidenceQuery = useQuery({
    queryKey: ['issue-evidence', organizationId, issueId, 'valid'],
    enabled: Boolean(organizationId && issueId && issueQuery.data && issueQuery.data.mode !== 'quick_correction'),
    queryFn: () => listIssueEvidence(organizationId!, issueId!, 'valid'),
  });

  const actionsQuery = useQuery({
    queryKey: ['issue-actions', organizationId, issueId],
    enabled: Boolean(organizationId && issueId),
    queryFn: () => IssueService.listLinkedActions(organizationId!, issueId!),
  });

  const { data: enterpriseProjects = [] } = useQuery({
    queryKey: ['enterprise-projects-list', organizationId],
    queryFn: () => ProjectService.list(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000,
  });

  const issue = issueQuery.data;
  const enriched = useMemo(() => (issue ? enrichIssue(issue) : null), [issue]);
  const linkedActions = actionsQuery.data ?? [];
  const history = useMemo(() => getIssueHistory(issue?.metadata), [issue?.metadata]);
  const nextAction = useMemo(
    () => (issue ? getIssueNextAction({
      ...issue,
      linked_actions_count: linkedActions.length,
      efficacy_ready: readyForEfficacyCheck(linkedActions, issue.status),
    }, {
      directActions: linkedActions.filter((action) => action.problem_id === issue.id),
      opportunityCount: issueOpportunities.data?.length ?? 0,
      actor: { id: user?.id, role: roleType },
    }) : null),
    [issue, linkedActions, issueOpportunities.data, user?.id, roleType],
  );
  const { data: linkedProject } = useQuery({
    queryKey: ['issue-project', organizationId, issue?.project_id],
    enabled: Boolean(organizationId && issue?.project_id),
    queryFn: () => ProjectService.getById(organizationId!, issue!.project_id!),
  });

  const assigneeNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of orgMembers) map[m.id] = m.name;
    return map;
  }, [orgMembers]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['issue', organizationId, issueId] });
    queryClient.invalidateQueries({ queryKey: ['issue-actions', organizationId, issueId] });
    queryClient.invalidateQueries({ queryKey: ['issues'] });
    queryClient.invalidateQueries({ queryKey: ['problem-dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['opportunities', organizationId] });
    queryClient.invalidateQueries({ queryKey: ['issue-masp-board', organizationId, issueId] });
    queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'analysis'] });
    queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'root'] });
    queryClient.invalidateQueries({ queryKey: ['issue-cause-check', organizationId, issueId] });
    queryClient.invalidateQueries({ queryKey: ['issue-countermeasures', organizationId, issueId] });
    queryClient.invalidateQueries({ queryKey: ['issue-masp-events', organizationId, issueId] });
    queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'exec'] });
    queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'valid'] });
    queryClient.invalidateQueries({ queryKey: ['enterprise-actions', organizationId] });
    queryClient.invalidateQueries({ queryKey: ['minha-mesa'] });
  }, [queryClient, organizationId, issueId]);

  const handleSaveRCA = useCallback(async (analysis: RootCauseAnalysis) => {
    if (!organizationId || !issueId) return;
    setRcaSaving(true);
    try {
      await IssueService.saveRootCauseAnalysis(organizationId, issueId, analysis);
      showToast('success', 'Análise salva');
      refresh();
      setEditView(null);
    } catch {
      showToast('error', 'Erro ao salvar');
    } finally {
      setRcaSaving(false);
    }
  }, [organizationId, issueId, refresh, showToast]);

  if (issueQuery.isLoading || actionsQuery.isLoading) {
    return <ModuleLayout ribbonConfig={null}><div className="flex items-center justify-center py-24 text-sm text-slate-400">Carregando workspace...</div></ModuleLayout>;
  }

  if (issueQuery.isError) {
    const code = typeof issueQuery.error === 'object' && issueQuery.error && 'code' in issueQuery.error
      ? String((issueQuery.error as { code?: string }).code)
      : '';
    return (
      <ModuleLayout ribbonConfig={null}>
        <div className="flex flex-col items-center py-24 gap-2">
          <p className="text-sm text-slate-500">{code === 'PGRST116' ? 'Problema não encontrado' : 'Não foi possível abrir o problema.'}</p>
          <button type="button" onClick={() => navigate('/problemas')} className="text-sm text-blue-600 hover:underline">Voltar à lista</button>
        </div>
      </ModuleLayout>
    );
  }

  if (!issue || !enriched) {
    return (
      <ModuleLayout ribbonConfig={null}>
        <div className="flex flex-col items-center py-24 gap-2">
          <p className="text-sm text-slate-500">Problema não encontrado</p>
          <button type="button" onClick={() => navigate('/problemas')} className="text-sm text-blue-600 hover:underline">Voltar à lista</button>
        </div>
      </ModuleLayout>
    );
  }

  const assigneeLabel = issue.assigned_to ? resolveIssueAssigneeName(issue.assigned_to, orgMembers) : 'Sem responsável';
  const goalId = issue.metadata?.linked_hoshin_goal_id as string | undefined;

  const handleTransition = async (newStatus: IssueStatus) => {
    try {
      if (issue.status === 'closed' && newStatus === 'open') {
        setReasonIntent('reopen');
        return;
      }
      if (newStatus === 'closed') {
        const storedReason = normalizeRequiredReason(issue.metadata?.closure_reason);
        const decision = evaluateIssueClosure({
          actions: linkedActions,
          attempts: efficacyAttempts(issue.metadata),
          closureReason: storedReason,
          metadata: issue.metadata,
        });
        if (!decision.canClose && !decision.requiresClosureReason) {
          showToast('error', decision.reason ?? 'O problema não pode ser encerrado.');
          return;
        }
        if (!storedReason && decision.requiresClosureReason) {
          setReasonIntent('close');
          return;
        }
        if (!decision.canClose) {
          showToast('error', decision.reason ?? 'O problema não pode ser encerrado.');
          return;
        }
      }
      await IssueService.transitionStatus(organizationId!, issueId!, newStatus, {
        actorId: user?.id,
        actorName: user?.name,
      });
      showToast('success', ISSUE_STATUS_LABELS[newStatus]);
      refresh();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Transição não permitida');
    }
  };

  const confirmReason = async (raw: string) => {
    const reason = normalizeRequiredReason(raw);
    const intent = reasonIntent;
    if (!reason || !intent || !organizationId || !issueId || !issue) return;
    setReasonIntent(null);
    try {
      if (intent === 'reopen') {
        await IssueService.transitionStatus(organizationId, issueId, 'open', {
          reopenReason: reason,
          actorId: user?.id,
          actorName: user?.name,
        });
        showToast('success', ISSUE_STATUS_LABELS.open);
      } else if (intent === 'save') {
        const patch = closureReasonPatch(issue.metadata, reason);
        if (!patch) return;
        await IssueService.update(organizationId, issueId, patch, {
          actorId: user?.id,
          actorName: user?.name,
          historyMessage: 'Motivo do encerramento registrado',
          historyType: 'note',
        });
        showToast('success', 'Motivo do encerramento registrado');
      } else {
        await IssueService.transitionStatus(organizationId, issueId, 'closed', {
          closureReason: reason,
          actorId: user?.id,
          actorName: user?.name,
        });
        showToast('success', ISSUE_STATUS_LABELS.closed);
      }
      refresh();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Não foi possível registrar o motivo');
    }
  };

  const occurredOn = localToday();
  const maspEditors: MaspEditors | undefined = organizationId && issueId && issue.mode !== 'quick_correction'
    ? {
      pending: maspQuery.isLoading || evidenceQuery.isLoading || causeEvidenceQuery.isLoading || causeCheckQuery.isLoading,
      loadError: maspQuery.isError || evidenceQuery.isError || causeEvidenceQuery.isError || causeCheckQuery.isError,
      causeEvidence: causeEvidenceQuery.data ?? [],
      causeCheck: causeCheckQuery.data ?? null,
      onAddHypothesisEvidence: async (analysis, hypothesisId, file, caption) => {
        if (!user?.id) throw new Error('Entre novamente para anexar a evidência.');
        await IssueService.saveRootCauseAnalysis(organizationId, issueId, analysis);
        await addIssueEvidence({
          organizationId,
          issueId,
          stage: 'root',
          hypothesisId,
          file,
          caption,
          createdBy: user.id,
        });
        refresh();
      },
      onDeleteHypothesisEvidence: async (record) => {
        await deleteIssueEvidence(record);
        refresh();
      },
      onSaveCauseCheck: async (check) => {
        if (!user?.id) throw new Error('Entre novamente para registrar o teste.');
        await saveCauseCheck({
          organizationId,
          issueId,
          reviewedBy: user.id,
          check,
        });
        queryClient.invalidateQueries({ queryKey: ['issue-cause-check', organizationId, issueId] });
      },
      measures: maspQuery.data?.measures ?? [],
      strata: maspQuery.data?.strata ?? [],
      gemba: maspQuery.data?.gemba ?? [],
      evidence: evidenceQuery.data ?? [],
      onAddLoss: async (input) => {
        await createLossMeasure({ organizationId, issueId, ...input, occurredOn });
        refresh();
      },
      onSaveControl: async (input) => {
        await createControlMeasure({ organizationId, issueId, ...input, occurredOn });
        refresh();
      },
      onRemoveMeasure: async (id) => {
        await deleteIssueMeasure(organizationId, id);
        refresh();
      },
      onAddStratum: async (dimension) => {
        await createStratum({
          organizationId,
          issueId,
          dimension,
          position: maspQuery.data?.strata.length ?? 0,
        });
        refresh();
      },
      onRemoveStratum: async (id) => {
        await deleteStratum(organizationId, id);
        refresh();
      },
      onAddSlice: async (input) => {
        await createSlice({ organizationId, issueId, ...input });
        refresh();
      },
      onRemoveSlice: async (id) => {
        await deleteSlice(organizationId, id);
        refresh();
      },
      onAddGemba: async (input) => {
        if (!user?.id) throw new Error('Entre novamente para registrar a observação.');
        await createGembaNote({
          organizationId,
          issueId,
          fact: input.fact,
          place: input.place,
          observedAt: `${input.observedOn}T12:00:00.000Z`,
          observerId: user.id,
        });
        refresh();
      },
      onRemoveGemba: async (id) => {
        await deleteGembaNote(organizationId, id);
        refresh();
      },
      onAddEvidence: async (file, caption) => {
        if (!user?.id) throw new Error('Entre novamente para anexar a evidência.');
        await addIssueEvidence({
          organizationId,
          issueId,
          stage: 'analysis',
          file,
          caption,
          createdBy: user.id,
        });
        refresh();
      },
      onDeleteEvidence: async (record) => {
        await deleteIssueEvidence(record);
        refresh();
      },
      onBeginCause: () => { void handleTransition('root_cause'); },
      onBeginObservation: () => { void handleTransition('investigation'); },
    }
    : undefined;

  const handleAssignMember = async (userId: string) => {
    try {
      const member = orgMembers.find(item => item.id === userId);
      await IssueService.update(organizationId!, issueId!, { assigned_to: userId }, {
        actorId: user?.id,
        actorName: user?.name,
        historyMessage: member ? `Responsável: ${member.name}` : 'Responsável definido',
        historyType: 'assigned',
      });
      showToast('success', 'Responsável definido');
      refresh();
    } catch {
      showToast('error', 'Não foi possível atribuir');
    }
  };

  const modals = (
    <>
      <ProblemReasonModal
        open={reasonIntent != null}
        mode={reasonIntent === 'reopen' ? 'reopen' : 'close'}
        onClose={() => setReasonIntent(null)}
        onConfirm={confirmReason}
      />
      <ProblemActionDrawer actionId={drawerActionId} organizationId={organizationId!} issueId={issueId!} onClose={() => setDrawerActionId(null)} onUpdated={refresh} />
      <NewActionModal
        open={showNewActionModal || editingActionId != null}
        onClose={() => { setShowNewActionModal(false); setEditingActionId(null); }}
        organizationId={organizationId!}
        problemId={issueId}
        originLabel={issue.title}
        variant={issue.mode === 'quick_correction' ? 'default' : 'countermeasure'}
        editing={(() => {
          const action = linkedActions.find((item) => item.id === editingActionId);
          if (!action) return null;
          const extension = (countermeasuresQuery.data ?? []).find((item) => item.actionId === action.id);
          return {
            action,
            whereText: extension?.whereText ?? null,
            howText: extension?.howText ?? null,
            costAmount: extension?.costAmount ?? null,
            costCurrency: extension?.costCurrency ?? null,
            hasExtension: Boolean(extension),
          };
        })()}
        orgMembers={orgMembers}
        enterpriseProjects={enterpriseProjects}
        showToast={showToast}
        onCreated={() => { setEditingActionId(null); refresh(); }}
      />
      <NewOpportunityModal
        open={showOpportunityModal}
        organizationId={organizationId!}
        createdBy={user?.id}
        members={orgMembers}
        origin={problemOpportunityDraft(issue)}
        existingNotice={openOpportunityNotice(openOpportunityCount(issueOpportunities.data ?? []))}
        onClose={() => setShowOpportunityModal(false)}
        onCreated={async (created: Opportunity) => {
          try {
            await IssueService.appendHistory(
              organizationId!,
              issueId!,
              'Oportunidade criada',
              'opportunity_linked',
              {
                actorId: user?.id,
                actorName: user?.name,
                meta: {
                  event: 'opportunity_created',
                  opportunity_id: created.id,
                  opportunity_title: created.title,
                },
              },
            );
          } catch {
            showToast('error', 'A oportunidade foi criada, mas o histórico do problema não foi atualizado.');
          }
          showToast('success', 'Oportunidade de melhoria criada');
          refresh();
        }}
        onError={(message) => showToast('error', message)}
      />
      <ToastContainer />
    </>
  );

  if (editView === 'causa') {
    return (
      <ModuleLayout ribbonConfig={null}>
        <EditShell title="Assistente de Causa Raiz" onBack={() => setEditView(null)}>
          <RootCauseWizard value={issue.root_cause_analysis ?? null} onSave={handleSaveRCA} onCreateActions={() => setShowNewActionModal(true)} saving={rcaSaving} />
        </EditShell>
        {modals}
      </ModuleLayout>
    );
  }

  if (editView === 'acoes') {
    return (
      <ModuleLayout ribbonConfig={null}>
        <EditShell title="Plano de Ação Corretivo" onBack={() => setEditView(null)}>
          <ProblemActionsTable actions={linkedActions} assigneeNames={assigneeNames} onRowClick={(a: EnterpriseAction) => setDrawerActionId(a.id)} onCreateAction={() => setShowNewActionModal(true)} />
        </EditShell>
        {modals}
      </ModuleLayout>
    );
  }

  if (editView === 'execucao') {
    return (
      <ModuleLayout ribbonConfig={null}>
        <EditShell title="Execução" onBack={() => setEditView(null)}>
          <ProblemExecutionPanel projectName={linkedProject?.name ?? null} workflowLinked={!!issue.workflow_id} actions={linkedActions} onNavigateProject={() => issue.project_id && navigate(`/projetos/${issue.project_id}`)} />
        </EditShell>
        {modals}
      </ModuleLayout>
    );
  }

  if (editView === 'validacao') {
    return (
      <ModuleLayout ribbonConfig={null}>
        <EditShell title="Validação e Monitoramento" onBack={() => setEditView(null)}>
          <ProblemValidationWizard
            validationConfig={issue.validation_config ?? null}
            monitoringConfig={issue.monitoring_config ?? null}
            issueStatus={issue.status}
            onSaveValidation={async c => { await IssueService.saveValidationConfig(organizationId!, issueId!, c); refresh(); }}
            onSaveMonitoring={async c => { await IssueService.saveMonitoringConfig(organizationId!, issueId!, c); refresh(); }}
            onCompleteValidation={async r => { await IssueService.completeValidation(organizationId!, issueId!, r); refresh(); }}
          />
        </EditShell>
        {modals}
      </ModuleLayout>
    );
  }

  if (editView === 'historico') {
    return (
      <ModuleLayout ribbonConfig={null}>
        <EditShell title="Histórico de Governança" onBack={() => setEditView(null)}>
          <ProblemTimeline entries={history} />
        </EditShell>
        {modals}
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout ribbonConfig={null}>
      <div className="flex h-full min-h-0 justify-center overflow-hidden bg-white dark:bg-slate-950">
        <div className="flex h-full min-h-0 w-full flex-col px-4 sm:px-6">
        <ProblemWorkspaceHeader
          issue={issue}
          enriched={enriched}
          assigneeLabel={assigneeLabel}
          onBack={() => navigate('/problemas')}
        />

        {nextAction && (
          <ProblemCaseWorkspace
            issue={issue}
            actions={linkedActions}
            assigneeNames={assigneeNames}
            members={orgMembers}
            nextAction={nextAction}
            projectName={linkedProject?.name ?? null}
            workflowLinked={Boolean(issue.workflow_id)}
            goalLinked={Boolean(goalId)}
            history={history}
            onAssign={handleAssignMember}
            onCreateAction={() => setShowNewActionModal(true)}
            onOpenCause={() => setEditView('causa')}
            onOpenActions={() => setEditView('acoes')}
            onOpenValidation={() => setEditView('validacao')}
            onOpenAction={action => setDrawerActionId(action.id)}
            onOpenProject={() => issue.project_id && navigate(`/projetos/${issue.project_id}`)}
            onSaveAnalysis={handleSaveRCA}
            savingAnalysis={rcaSaving}
            onTransition={handleTransition}
            onInformClosureReason={() => setReasonIntent('save')}
            opportunities={issueOpportunities.data ?? []}
            canClose={canCloseIssue({
              actions: linkedActions.filter((action) => action.problem_id === issue.id),
              attempts: efficacyAttempts(issue.metadata),
              closureReason: issue.metadata?.closure_reason,
              metadata: issue.metadata,
            }).allowed}
            hasIneffectiveCheck={efficacyAttempts(issue.metadata).some((attempt) => attempt.result === 'ineffective')}
            onCreateOpportunity={() => setShowOpportunityModal(true)}
            onOpenOpportunity={(opportunityId) => navigate(`/oportunidades?id=${encodeURIComponent(opportunityId)}`)}
            assigneeLabel={assigneeLabel}
            masp={maspEditors}
            actionsState={actionsQuery.isError ? 'error' : undefined}
            eventsState={standardizationEventsQuery.isError ? 'error' : undefined}
            executionEventsState={executionEventsQuery.isError ? 'error' : undefined}
            countermeasures={countermeasuresQuery.data ?? []}
            onEditCountermeasure={(action) => setEditingActionId(action.id)}
            onCancelCountermeasure={async (action) => {
              const updated = await ActionService.update(action.id, { status: 'cancelled' });
              if (!updated) {
                showToast('error', 'Não foi possível cancelar a contramedida.');
                return;
              }
              await IssueService.appendHistory(organizationId!, issueId!, `Contramedida cancelada: ${action.title}`, 'action_linked', {
                actorId: user?.id,
                actorName: user?.name,
              });
              refresh();
            }}
            executionEvents={executionEventsQuery.data ?? []}
            standardizationEvents={standardizationEventsQuery.data ?? []}
            onAddStandardizationEvent={async (input) => {
              await createStandardizationEvent({
                organizationId: organizationId!,
                issueId: issueId!,
                kind: input.kind,
                title: input.title,
                body: input.body,
                audience: input.audience,
                result: input.result,
                occurredOn: input.occurredOn || null,
                actorId: user?.id ?? null,
              });
              await queryClient.invalidateQueries({ queryKey: ['issue-masp-events', organizationId, issueId] });
            }}
            executionEvidence={executionEvidenceQuery.data ?? []}
            onChangeActionStatus={async (action, status) => {
              const updated = await ActionService.update(action.id, { status });
              if (!updated) {
                showToast('error', 'Não foi possível atualizar a contramedida.');
                return;
              }
              if (status === 'completed') {
                await IssueService.appendHistory(organizationId!, issueId!, `Ação concluída: ${action.title}`, 'action_linked', {
                  actorId: user?.id,
                  actorName: user?.name,
                });
                await IssueService.syncLinkedActionsCount(organizationId!, issueId!);
              }
              if (status === 'cancelled') {
                await IssueService.appendHistory(organizationId!, issueId!, `Contramedida cancelada: ${action.title}`, 'action_linked', {
                  actorId: user?.id,
                  actorName: user?.name,
                });
              }
              refresh();
            }}
            onAddExecutionEvent={async (input) => {
              await createIssueMaspEvent({
                organizationId: organizationId!,
                issueId: issueId!,
                kind: input.kind,
                title: input.title,
                body: input.body,
                occurredOn: input.occurredOn || null,
                actorId: user?.id ?? null,
              });
              await queryClient.invalidateQueries({ queryKey: ['issue-masp-events', organizationId, issueId] });
            }}
            onAddExecutionEvidence={async (file, caption) => {
              if (!user?.id) throw new Error('Entre novamente para anexar a evidência.');
              await addIssueEvidence({
                organizationId: organizationId!,
                issueId: issueId!,
                stage: 'exec',
                hypothesisId: null,
                file,
                caption,
                createdBy: user.id,
              });
              await queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'exec'] });
            }}
            onDeleteExecutionEvidence={async (record) => {
              await deleteIssueEvidence(record);
              await queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'exec'] });
            }}
            validationEvidence={validationEvidenceQuery.data ?? []}
            actorId={user?.id}
            actorRole={roleType}
            onAddAfterPoint={async (measureId, value, occurredOn) => {
              await addMeasurePoint({
                organizationId: organizationId!,
                issueId: issueId!,
                measureId,
                pointKind: 'after',
                value,
                occurredOn,
              });
              await queryClient.invalidateQueries({ queryKey: ['issue-masp-board', organizationId, issueId] });
            }}
            onConfirmEfficacy={async (note) => {
              await IssueService.confirmEfficacy(organizationId!, issueId!, note, { id: user?.id, name: user?.name, role: roleType });
              refresh();
            }}
            onRejectEfficacy={async (note) => {
              await IssueService.rejectEfficacy(organizationId!, issueId!, note, { id: user?.id, name: user?.name, role: roleType });
              refresh();
            }}
            onAddValidationEvidence={async (file, caption) => {
              if (!user?.id) throw new Error('Entre novamente para anexar a evidência.');
              await addIssueEvidence({
                organizationId: organizationId!,
                issueId: issueId!,
                stage: 'valid',
                hypothesisId: null,
                file,
                caption,
                createdBy: user.id,
              });
              await queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'valid'] });
            }}
            onDeleteValidationEvidence={async (record) => {
              await deleteIssueEvidence(record);
              await queryClient.invalidateQueries({ queryKey: ['issue-evidence', organizationId, issueId, 'valid'] });
            }}
            efficacyPanel={nextAction ? (
              <EfficacyVerificationPanel
                embedded
                showResolutionNote={workspaceShowsResolutionCaveat(
                  linkedActions,
                  workspaceWorkFocus(issue, nextAction),
                )}
                issue={issue}
                actions={linkedActions}
                members={orgMembers}
                actorId={user?.id}
                actorRole={roleType}
                onConfirm={async (note) => {
                  await IssueService.confirmEfficacy(organizationId!, issueId!, note, { id: user?.id, name: user?.name, role: roleType });
                  showToast('success', EFFICACY_CONFIRMATION_CLOSES_ISSUE
                    ? 'Eficácia confirmada. O problema foi encerrado.'
                    : 'Eficácia confirmada. O problema continua aberto até o encerramento.');
                  refresh();
                }}
                onReject={async (note) => {
                  await IssueService.rejectEfficacy(organizationId!, issueId!, note, { id: user?.id, name: user?.name, role: roleType });
                  showToast('info', 'Verificação registrada. O problema continua aberto.');
                  refresh();
                }}
                onCreateAction={() => setShowNewActionModal(true)}
                onSetVerifier={async (userId) => {
                  await IssueService.update(organizationId!, issueId!, {
                    metadata: { ...(issue.metadata ?? {}), verification_owner_id: userId ?? undefined },
                  });
                  refresh();
                }}
                onSetExplicitDue={async (isoDate) => {
                  await IssueService.update(organizationId!, issueId!, {
                    metadata: { ...(issue.metadata ?? {}), verification_due_at: isoDate ?? undefined },
                  });
                  refresh();
                }}
              />
            ) : null}
          />
        )}
        </div>
      </div>
      {modals}
    </ModuleLayout>
  );
};

function localToday(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function EditShell({ title, onBack, children }: { title: string; onBack: () => void; children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col bg-slate-50 dark:bg-slate-950">
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><ArrowLeft size={14} /> Ficha</button>
        <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">{title}</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </div>
  );
}

export default ProblemWorkspacePage;
